import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  IconRoute,
  IconStar,
  IconStarFilled,
  IconMoodSad,
  IconMoodConfuzed,
  IconMoodEmpty,
  IconMoodSmile,
  IconMoodHappy,
} from '@tabler/icons-react';
import type { CardSurvey, CardQuestion, SurveyPath } from '../types';
import { normalizeTriggerValues } from '../utils/pathTriggerValues';

const SATISFACTION_ICONS = [
  IconMoodSad,
  IconMoodConfuzed,
  IconMoodEmpty,
  IconMoodSmile,
  IconMoodHappy,
];

interface SurveyPreviewProps {
  survey: CardSurvey | null;
}

type PathState = { path: SurveyPath; questionIndex: number };

export function SurveyPreview({ survey }: SurveyPreviewProps) {
  const instanceId = React.useId();
  const [mainIndex, setMainIndex] = useState(0);
  const [pathState, setPathState] = useState<PathState | null>(null);
  const [ratingValues, setRatingValues] = useState<Record<string, number>>({});
  const [radioValues, setRadioValues] = useState<Record<string, string>>({});
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [checkboxValues, setCheckboxValues] = useState<Record<string, string[]>>({});

  const { mainQuestions, pathsByTrigger, pathQuestionsMap } = useMemo(() => {
    if (!survey?.questions?.length) {
      return {
        mainQuestions: [] as CardQuestion[],
        pathsByTrigger: new Map<string, SurveyPath[]>(),
        pathQuestionsMap: new Map<string, CardQuestion[]>(),
      };
    }
    const questions = survey.questions;
    const questionById = new Map(questions.map((q) => [q.id, q]));
    const main = questions.filter((q) => !q.pathId);
    const byTrigger = new Map<string, SurveyPath[]>();
    (survey.paths || []).forEach((path) => {
      const triggerId = path.trigger.questionId;
      if (!byTrigger.has(triggerId)) byTrigger.set(triggerId, []);
      byTrigger.get(triggerId)!.push(path);
    });
    const pathMap = new Map<string, CardQuestion[]>();
    (survey.paths || []).forEach((path) => {
      const qs = (path.questionIds || [])
        .map((id) => questionById.get(id))
        .filter((q): q is CardQuestion => q != null);
      pathMap.set(path.id, qs);
    });
    return { mainQuestions: main, pathsByTrigger: byTrigger, pathQuestionsMap: pathMap };
  }, [survey]);

  useEffect(() => {
    setMainIndex(0);
    setPathState(null);
    setRatingValues({});
    setRadioValues({});
    setTextValues({});
    setCheckboxValues({});
  }, [survey]);

  const currentQuestion: CardQuestion | null = useMemo(() => {
    if (pathState) {
      const pathQuestions = pathQuestionsMap.get(pathState.path.id) || [];
      return pathQuestions[pathState.questionIndex] ?? null;
    }
    return mainQuestions[mainIndex] ?? null;
  }, [pathState, mainIndex, mainQuestions, pathQuestionsMap]);

  const isInPath = pathState !== null;
  const pathQuestions = pathState ? pathQuestionsMap.get(pathState.path.id) || [] : [];

  const advanceToNext = useCallback(
    (answerForBranch?: string | string[]) => {
      if (pathState) {
        const nextIndex = pathState.questionIndex + 1;
        if (nextIndex < pathQuestions.length) {
          setPathState({ ...pathState, questionIndex: nextIndex });
        } else {
          setPathState(null);
          setMainIndex((i) => i + 1);
        }
        return;
      }
      const question = mainQuestions[mainIndex];
      if (!question) return;
      const triggerPaths = pathsByTrigger.get(question.id) || [];
      const chosen = Array.isArray(answerForBranch) ? answerForBranch : answerForBranch != null ? [answerForBranch] : [];
      const matchingPath = triggerPaths.find((path) => {
        const vals = normalizeTriggerValues(path.trigger.values);
        return chosen.some((v) => vals.includes(v));
      });
      if (matchingPath) {
        const pq = pathQuestionsMap.get(matchingPath.id) || [];
        if (pq.length > 0) {
          setPathState({ path: matchingPath, questionIndex: 0 });
          return;
        }
      }
      setMainIndex((i) => i + 1);
    },
    [pathState, mainIndex, mainQuestions, pathsByTrigger, pathQuestionsMap, pathQuestions.length]
  );

  const handleRadioChange = useCallback(
    (questionId: string, value: string) => {
      setRadioValues((prev) => ({ ...prev, [questionId]: value }));
      advanceToNext(value);
    },
    [advanceToNext]
  );

  const handleCheckboxToggle = useCallback((questionId: string, optionValue: string, checked: boolean) => {
    setCheckboxValues((prev) => {
      const arr = prev[questionId] ?? [];
      const next = checked ? [...arr, optionValue] : arr.filter((v) => v !== optionValue);
      return { ...prev, [questionId]: next };
    });
  }, []);

  const handleCheckboxNext = useCallback(
    (questionId: string) => {
      const selected = checkboxValues[questionId] ?? [];
      advanceToNext(selected);
    },
    [advanceToNext, checkboxValues]
  );

  const handleRatingChange = useCallback(
    (questionId: string, value: number) => {
      setRatingValues((prev) => ({ ...prev, [questionId]: value }));
      advanceToNext();
    },
    [advanceToNext]
  );

  const handleTextNext = useCallback(() => advanceToNext(), [advanceToNext]);

  const handleRestart = useCallback(() => {
    setMainIndex(0);
    setPathState(null);
    setRatingValues({});
    setRadioValues({});
    setTextValues({});
    setCheckboxValues({});
  }, []);

  if (!survey) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No survey to preview. Add questions in the Survey Builder.
      </p>
    );
  }

  if (mainQuestions.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No questions yet. Add questions in the Survey Builder to see a preview.
      </p>
    );
  }

  const atEnd = !currentQuestion && mainIndex >= mainQuestions.length;

  if (atEnd) {
    return (
      <div className="py-4 text-center">
        <h6 className="mb-1 text-lg font-semibold text-primary">
          End of survey
        </h6>
        <p className="mb-2 text-sm text-muted-foreground">
          Thank you for completing the preview.
        </p>
        <Button variant="outline" onClick={handleRestart}>
          Restart preview
        </Button>
      </div>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  const options = currentQuestion.config?.options ?? [];
  const ratingFormat = currentQuestion.config?.format ?? 'numerical';
  const rateAmount = currentQuestion.config?.rateAmount ?? 5;

  return (
    <div className="py-2">
      <p className="mb-2 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Step-by-step preview
      </p>
      {isInPath && pathState && (
        <p className="mb-1 flex items-center gap-1 text-xs text-primary">
          <IconRoute className="size-4" />
          Branch: {pathState.path.name || 'Path'}
        </p>
      )}
      <Card className="rounded-lg border bg-background p-3">
        <p className="mb-2 text-base font-semibold">
          {currentQuestion.type === 'done' ? 'done' : `${currentQuestion.number}. ${currentQuestion.text || 'Untitled question'}`}
          {currentQuestion.type !== 'done' && currentQuestion.required && (
            <span className="ml-1 text-destructive">*</span>
          )}
        </p>

        {currentQuestion.type === 'rating' && (
          <div className="mt-2">
            {ratingFormat === 'satisfaction' ? (
              <div className="flex flex-wrap items-center gap-1">
                {SATISFACTION_ICONS.map((Icon, i) => {
                  const value = i + 1;
                  const selected = (ratingValues[currentQuestion.id] ?? 0) >= value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRatingChange(currentQuestion.id, value)}
                      className={cn(
                        'cursor-pointer border-none bg-transparent p-1',
                        selected ? 'text-primary' : 'text-muted-foreground/40',
                        'hover:text-primary/80'
                      )}
                      aria-label={`${value} of 5`}
                    >
                      <Icon className="size-10" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-0.5">
                {Array.from({ length: rateAmount }, (_, i) => {
                  const value = i + 1;
                  const selected = (ratingValues[currentQuestion.id] ?? 0) >= value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRatingChange(currentQuestion.id, value)}
                      className={cn(
                        'cursor-pointer border-none bg-transparent p-0.5',
                        selected ? 'text-amber-400' : 'text-muted-foreground/30',
                        'hover:text-amber-500'
                      )}
                      aria-label={`${value} of ${rateAmount}`}
                    >
                      {selected ? (
                        <IconStarFilled className="size-10" />
                      ) : (
                        <IconStar className="size-10" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {currentQuestion.type === 'radio' && (
          <div className="mt-1 flex flex-col gap-1">
            {options.map((opt, i) => (
              <label
                key={i}
                className="flex w-full cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/50"
              >
                <input
                  type="radio"
                  name={`preview-radio-${instanceId}-${currentQuestion.id}`}
                  value={opt}
                  checked={radioValues[currentQuestion.id] === opt}
                  onChange={() => handleRadioChange(currentQuestion.id, opt)}
                  className="size-4 accent-primary"
                />
                <span className="cursor-pointer text-sm">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {currentQuestion.type === 'checkbox' && (
          <>
            <div className="mt-1 flex flex-col gap-1">
              {options.map((opt, i) => (
                <label key={i} className="flex items-center gap-2 px-1 py-0.5">
                  <Checkbox
                    checked={(checkboxValues[currentQuestion.id] ?? []).includes(opt)}
                    onCheckedChange={(checked) => handleCheckboxToggle(currentQuestion.id, opt, !!checked)}
                  />
                  <span className="text-sm">{opt}</span>
                </label>
              ))}
            </div>
            <Button size="sm" className="mt-2" onClick={() => handleCheckboxNext(currentQuestion.id)}>
              Next
            </Button>
          </>
        )}

        {currentQuestion.type === 'textfield' && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Input
              placeholder="Your answer..."
              value={textValues[currentQuestion.id] ?? ''}
              onChange={(e) => setTextValues((prev) => ({ ...prev, [currentQuestion.id]: e.target.value }))}
              className="min-w-[200px] max-w-[400px] flex-1"
            />
            <Button size="sm" onClick={handleTextNext}>
              Next
            </Button>
          </div>
        )}

        {currentQuestion.type === 'done' && (
          <Button size="sm" className="mt-2" onClick={() => advanceToNext()}>
            Next
          </Button>
        )}
      </Card>
    </div>
  );
}
